import { getGlobalPromoTag, getPromoTagMarkup } from 'promo-tag-utils';
import { html, raw, safeUrl, safeImageUrl, cssToken } from 'html-utils';

window.BSS = window.BSS || {};
BSS.Components = BSS.Components || {};

// Vanilla JS cookie utilities
const cookieUtils = {
  set: function (name, value, options = {}) {
    let cookieString = `${name}=${encodeURIComponent(value)}`;

    if (options.expires) {
      const date = new Date();
      date.setTime(date.getTime() + options.expires * 24 * 60 * 60 * 1000);
      cookieString += `; expires=${date.toUTCString()}`;
    }

    if (options.path) cookieString += `; path=${options.path}`;
    if (options.domain) cookieString += `; domain=${options.domain}`;
    if (options.secure) cookieString += '; secure';

    document.cookie = cookieString;
  },

  get: function (name) {
    const cookies = document.cookie.split(';');
    for (let cookie of cookies) {
      const trimmedCookie = cookie.trim();
      if (trimmedCookie.startsWith(name + '=')) {
        return decodeURIComponent(trimmedCookie.substring(name.length + 1));
      }
    }
    return null;
  }
};

BSS.Components.RecentlyViewedProducts = (function () {
  const config = {
    howManyToShow: 4,
    howManyToStoreInMemory: 10
  };

  const PRODUCT_URL_REGEX = /\/products\/([a-z0-9\-]+)/;

  let productHandleQueue = [];
  let products = [];

  const cookie = {
    configuration: {
      expires: 90,
      path: '/',
      domain: window.location.hostname
    },
    name: 'shopify_recently_viewed',
    write: function (recentlyViewed) {
      cookieUtils.set(this.name, recentlyViewed.join(' '), this.configuration);
    },
    read: function () {
      const cookieValue = cookieUtils.get(this.name);
      return cookieValue ? cookieValue.split(/[ ]+/).filter(Boolean) : [];
    },
    destroy: function () {
      cookieUtils.set(this.name, '', { ...this.configuration, expires: -1 });
    },
    remove: function (productHandle) {
      const recentlyViewed = this.read().filter(
        handle => handle !== productHandle
      );
      this.write(recentlyViewed);
    }
  };

  const generateProductHTML = product => {
    // waitlistPromoTag is produced by getPromoTagMarkup, which escapes its inputs.
    const waitlistTag = raw(product.waitlistPromoTag || '');
    return html`
      <div
        class="recently-viewed-item recently-viewed-item--${cssToken(
          product.id
        )}"
      >
        <div class="product-card__tag-wrapper">
          <div class="product-card__tag">${waitlistTag}</div>
        </div>
        <a
          class="recently-viewed-link"
          href="${safeUrl(product.url, { fallback: '#' })}"
        >
          <div class="recently-viewed-image">
            <img
              src="${safeImageUrl(product.featured_image)}"
              alt="${product.title}"
              loading="lazy"
            />
          </div>
        </a>
      </div>
    `;
  };

  const generateSectionHTML = products => {
    const productItems = products.map(product => generateProductHTML(product));
    return html`
      <div class="product-recent">
        <div class="recently-viewed-container">
          <div class="recently-viewed-wrapper">
            <h2 class="recently-viewed-title js-recently-viewed-title">
              Recently Viewed
            </h2>
            <div class="recently-viewed-grid js-recently-viewed-grid">
              ${productItems}
            </div>
          </div>
        </div>
      </div>
    `;
  };

  const fetchAndRender = async function () {
    const currentPageProductHandle =
      window.location.pathname.match(PRODUCT_URL_REGEX)?.[1];

    // Filter queue to exclude current page product and already fetched products
    productHandleQueue = productHandleQueue.filter(handle => {
      return (
        handle &&
        handle !== currentPageProductHandle &&
        !products.some(p => p.handle === handle)
      );
    });

    // Batch fetch remaining products up to the limit
    const handlesToFetch = productHandleQueue.slice(
      0,
      config.howManyToShow - products.length
    );

    if (handlesToFetch.length === 0) {
      return render();
    }

    const fetchPromises = handlesToFetch.map(handle =>
      fetch(`/products/${encodeURIComponent(handle)}.js`)
        .then(response => {
          if (!response.ok) throw new Error('Product not found');
          return response.json();
        })
        .then(product => {
          // Add waitlist promo tag if the product has the 'waitlist' tag
          if (product?.tags?.includes('waitlist')) {
            const waitlistTag = getGlobalPromoTag('waitlist');
            if (waitlistTag) {
              product.waitlistPromoTag = getPromoTagMarkup(waitlistTag);
            }
          }
          return product;
        })
        .catch(() => {
          cookie.remove(handle);
          return null;
        })
    );

    const fetchedProducts = await Promise.all(fetchPromises);
    products.push(...fetchedProducts.filter(Boolean));

    render();
  };

  const render = () => {
    const container = document.getElementById('js-recently-viewed-products');

    if (!container) return;

    if (products.length) {
      container.innerHTML = generateSectionHTML(products);
    } else {
      container.parentElement?.remove();
    }

    recordRecentlyViewed();
  };

  const recordRecentlyViewed = () => {
    const productHandle =
      window.location.pathname.match(PRODUCT_URL_REGEX)?.[1];

    if (!productHandle) return;

    let recentlyViewed = cookie
      .read()
      .filter(handle => handle !== productHandle);
    recentlyViewed.unshift(productHandle);
    recentlyViewed = recentlyViewed.slice(0, config.howManyToStoreInMemory);

    cookie.write(recentlyViewed);
  };

  function init() {
    productHandleQueue = cookie.read();
    fetchAndRender();
  }

  return { init };
})();
